/**
 * The one module that talks to `advertisements` (0012), the two lifecycle
 * rpcs (0013) and the `feed_items` view (0014). Spec:
 * `plans/phase-4-advertise-feed.md` §1 Client (the `src/lib/ads.ts` row).
 *
 * Conventions, inherited from `src/lib/profile.ts` and `src/lib/roster-data.ts`:
 *
 * - every call returns a `{ data, error }`-shaped result with
 *   `PostgrestError | null` — no throws across the Supabase boundary;
 * - dates are `'YYYY-MM-DD'` strings end to end, **verbatim** — never through
 *   `new Date()`, which is a timezone waiting to happen;
 * - client-renderable failures come back as typed values, not bare errors,
 *   so the sheets render a sentence instead of a code (the `RosterDataError`
 *   idiom). Here the typed rejections are 0013's PZxxx errcodes, mirrored
 *   exactly; `AD_MESSAGES` is the sentence for each.
 *
 * The `no UPDATE grant / no client INSERT-with-status` stance means every
 * write below is an rpc into a SECURITY DEFINER function that owns its
 * status flips atomically and audits them — there is no `.update()` and no
 * `.insert()` on `advertisements` anywhere in this module, on purpose.
 */

import { PostgrestError } from '@supabase/supabase-js'
import { supabase } from './supabase'
import type { BlockCode, IsoDate, ShiftCode } from '../engine/types'

/** An ad's intent, D2 — display only; NEED_OFF still resolves as a 1:1 swap in Phase 5. */
export type AdIntent = 'OPEN_TO_SWAP' | 'NEED_OFF'

/** The statuses 0012 pre-declared so Phase 5 never ALTERs live data. */
export type AdStatus = 'OPEN' | 'PENDING' | 'SWAPPED' | 'WITHDRAWN'

// ---------------------------------------------------------------------------
// Typed rejections — 0013's errcode block, mirrored
// ---------------------------------------------------------------------------

/** Why a lifecycle call was refused, as a value the UI can sentence. */
export type AdRejection =
  | 'NOT_ACTIVE'
  | 'BAD_ARGS'
  | 'NOT_YOUR_SHIFT'
  | 'NOT_ROSTERED'
  | 'NOT_BLOCK_USER'
  | 'NOT_YOUR_DERIVED_SHIFT'
  | 'EXCEPTION_ON_DATE'
  | 'AD_ALREADY_LIVE'
  | 'NOT_YOUR_AD'
  | 'AD_NOT_OPEN'

/** One sentence per rejection — the reason always reaches the person (CLAUDE.md). */
export const AD_MESSAGES: Record<AdRejection, string> = {
  NOT_ACTIVE: 'Your account needs to be active to advertise. Contact an admin if this is a mistake.',
  BAD_ARGS: "That request didn't name a shift to advertise — close this and try again.",
  NOT_YOUR_SHIFT: "That shift isn't yours to advertise.",
  NOT_ROSTERED: 'That shift is already advertised or swapped — refresh your calendar and check its state.',
  NOT_BLOCK_USER:
    'Your profile has no block, so there is no rostered shift to advertise — add the shift first, then advertise it.',
  NOT_YOUR_DERIVED_SHIFT:
    "Your block doesn't work that shift on that day, so it can't be advertised.",
  EXCEPTION_ON_DATE:
    "You've marked that day leave / unavailable / not working, so its shift can't be advertised.",
  AD_ALREADY_LIVE: 'That shift is already advertised.',
  NOT_YOUR_AD: "That advertisement isn't yours to withdraw.",
  AD_NOT_OPEN: 'Only an open advertisement can be withdrawn — refresh and check its state.',
}

/** Maps an rpc's PostgrestError to a typed rejection, or null if it isn't one. */
function rejectionFor(error: PostgrestError, codes: Record<string, AdRejection>): AdRejection | null {
  return codes[error.code] ?? null
}

/** Every outcome a lifecycle call can produce: refused-with-a-reason, errored, or done. */
export type AdCallResult =
  | { ok: true; rejection: null; error: null }
  | { ok: false; rejection: AdRejection; error: null }
  | { ok: false; rejection: null; error: PostgrestError }

// ---------------------------------------------------------------------------
// create / withdraw — the rpcs
// ---------------------------------------------------------------------------

/**
 * What `createAdvertisement` needs: exactly one of `shiftId` (an existing
 * explicit row) or `workDate` + `shiftCode` (a derived block shift with no
 * row yet — 0013 materialises it). The NS flag (B7) is display in this phase;
 * its *enforcement* is Phase 5's. The note is optional free text.
 */
export interface CreateAdvertisementInput {
  shiftId?: string
  workDate?: IsoDate
  shiftCode?: ShiftCode
  noNightSwap: boolean
  intent: AdIntent
  note?: string | null
}

/**
 * Advertises one shift via `create_advertisement()` (0013). On success the
 * shift is `ADVERTISED`, the ad is `OPEN`, and an `ADVERTISE` audit row
 * exists — all atomically, or none of it. A typed rejection comes back as
 * `{ ok: false, rejection, error: null }`; anything else is a `PostgrestError`
 * for the caller's generic error sentence.
 */
export async function createAdvertisement(
  input: CreateAdvertisementInput,
): Promise<AdCallResult> {
  const { error } = await supabase.rpc('create_advertisement', {
    p_shift_id: input.shiftId ?? null,
    p_work_date: input.workDate ?? null,
    p_shift_code: input.shiftCode ?? null,
    p_no_night_swap: input.noNightSwap,
    p_intent: input.intent,
    p_note: input.note ?? null,
  })
  if (error === null) return { ok: true, rejection: null, error: null }

  const rejection = rejectionFor(error, {
    PZ00A: 'NOT_ACTIVE',
    PZ007: 'BAD_ARGS',
    PZ001: 'NOT_YOUR_SHIFT',
    PZ002: 'NOT_ROSTERED',
    PZ003: 'NOT_BLOCK_USER',
    PZ004: 'NOT_YOUR_DERIVED_SHIFT',
    PZ005: 'EXCEPTION_ON_DATE',
    PZ006: 'AD_ALREADY_LIVE',
  })
  if (rejection !== null) return { ok: false, rejection, error: null }
  return { ok: false, rejection: null, error }
}

/**
 * Withdraws an open ad via `withdraw_advertisement()` (0013): the ad goes
 * `WITHDRAWN`, the shift back to `ROSTERED`, a `WITHDRAW_AD` audit row lands,
 * atomically. An undo of an advertising, not of a swap. Re-advertising later
 * re-opens the same row.
 */
export async function withdrawAdvertisement(
  advertisementId: string,
): Promise<AdCallResult> {
  const { error } = await supabase.rpc('withdraw_advertisement', {
    p_advertisement_id: advertisementId,
  })
  if (error === null) return { ok: true, rejection: null, error: null }

  const rejection = rejectionFor(error, {
    PZ00A: 'NOT_ACTIVE',
    PZ001: 'NOT_YOUR_AD',
    PZ008: 'AD_NOT_OPEN',
  })
  if (rejection !== null) return { ok: false, rejection, error: null }
  return { ok: false, rejection: null, error }
}

// ---------------------------------------------------------------------------
// The feed — the view's own column list, as a type
// ---------------------------------------------------------------------------

/**
 * One row of `feed_items` (0014), exactly the columns the view exposes and
 * not one more — the column list *is* the peer surface, which is why
 * `rls/feed.test.ts` asserts `select('*')` against these names. Dates are
 * verbatim `'YYYY-MM-DD'`; `createdAt` is the timestamptz string PostgREST
 * returns, carried untouched.
 */
export interface FeedItem {
  advertisementId: string
  workDate: IsoDate
  shiftCode: ShiftCode
  ownerUserId: string
  displayName: string | null
  blockCode: BlockCode | null
  classification: string | null
  facebookUsername: string | null
  intent: AdIntent
  noNightSwap: boolean
  note: string | null
  createdAt: string
}

/** A `feed_items` row as PostgREST returns it — snake_case, dates verbatim. */
interface FeedItemRow {
  advertisement_id: string
  work_date: IsoDate
  shift_code: ShiftCode
  owner_user_id: string
  display_name: string | null
  block_code: BlockCode | null
  classification: string | null
  facebook_username: string | null
  intent: AdIntent
  no_night_swap: boolean
  note: string | null
  created_at: string
}

function toFeedItem(row: FeedItemRow): FeedItem {
  return {
    advertisementId: row.advertisement_id,
    workDate: row.work_date,
    shiftCode: row.shift_code,
    ownerUserId: row.owner_user_id,
    displayName: row.display_name,
    blockCode: row.block_code,
    classification: row.classification,
    facebookUsername: row.facebook_username,
    intent: row.intent,
    noNightSwap: row.no_night_swap,
    note: row.note,
    createdAt: row.created_at,
  }
}

/**
 * Every OPEN ad the viewer's policies let them see. No date filter here, by
 * the plan's ruling: the server's `current_date` is UTC and the app's today
 * is ACT wall-clock, so the client (feed.ts) filters with its own `today`.
 */
export async function fetchFeedItems(): Promise<{
  data: FeedItem[] | null
  error: PostgrestError | null
}> {
  const { data, error } = await supabase.from('feed_items').select('*')
  if (error) return { data: null, error }
  return { data: (data as FeedItemRow[]).map(toFeedItem), error: null }
}

// ---------------------------------------------------------------------------
// Own ads — the calendar badge and the day sheet
// ---------------------------------------------------------------------------

/** One of the viewer's own ads, joined to the shift it advertises. */
export interface MyAdvertisement {
  id: string
  shiftId: string
  date: IsoDate
  code: ShiftCode
  status: AdStatus
  noNightSwap: boolean
  intent: AdIntent
  note: string | null
}

/** An `advertisements ⋈ shifts` row as PostgREST returns the embedded join. */
interface MyAdvertisementRow {
  id: string
  shift_id: string
  status: AdStatus
  no_night_swap: boolean
  intent: AdIntent
  note: string | null
  shift: { work_date: IsoDate; shift_code: ShiftCode }
}

function toMyAdvertisement(row: MyAdvertisementRow): MyAdvertisement {
  return {
    id: row.id,
    shiftId: row.shift_id,
    date: row.shift.work_date,
    code: row.shift.shift_code,
    status: row.status,
    noNightSwap: row.no_night_swap,
    intent: row.intent,
    note: row.note,
  }
}

/**
 * The viewer's own advertisements, every status — the calendar badge needs
 * the OPEN ones and Phase 5 will need the rest. Own rows only: filtered by
 * `owner_user_id` explicitly (never trusting RLS alone to narrow the
 * statement), matching `fetchRosterData`'s convention.
 */
export async function fetchMyAdvertisements(userId: string): Promise<{
  data: MyAdvertisement[] | null
  error: PostgrestError | null
}> {
  const { data, error } = await supabase
    .from('advertisements')
    .select('id, shift_id, status, no_night_swap, intent, note, shift:shifts(work_date, shift_code)')
    .eq('owner_user_id', userId)
  if (error) return { data: null, error }
  return { data: (data as unknown as MyAdvertisementRow[]).map(toMyAdvertisement), error: null }
}
